import { useQueryClient } from "@tanstack/react-query";
import { Select } from "antd";
import { isFieldSortable } from "@evolved/domain";
import { viewTypes } from "@evolved/constants";
import { sortData } from "@evolved/views";
import { useMemo } from "react";
import { useLocalStorage } from "react-use";

import { CreateOpportunityModal } from "components/modals/create-opportunity-modal";
import { OpportunitiesList } from "components/lists/opportunities";
import { root as accountsRoot } from "data/use-accounts";
import { useModal } from "hooks/use-modal";
import { useFieldConfigs } from "components/view/use-field-configs";
import { filterOption } from "components/selects/filter-option";
import { useOpportunities } from "data/use-opportunities";
import { useLossReasons } from "data/use-loss-reasons";
import { useOpportunityStates } from "data/use-opportunity-states";
import { useProducts } from "data/use-products";
import { useSalesProcesses } from "data/use-sales-processes";
import { useUsers } from "data/use-users";
import { useOrganization } from "data/use-organization";

const DEFAULT_SORT = {
  dataIndex: "startedOn",
  order: "descend",
};

const SORT_ORDER_OPTIONS = [
  { value: "ascend", label: "Ascending" },
  { value: "descend", label: "Descending" },
];

const SORTABLE_FIELD_ORDER = [
  "alias",
  "stateId",
  "lastActedOn",
  "nextFollowupOn",
  "targetWinDate",
  "wonDate",
  "createdOn",
  "startedOn",
  "lastStateChangeOn",
  "value",
  "weightedRevenue",
  "funnelProgress",
  "salesProcessId",
  "initialStateId",
  "lossReasonId",
  "accountId",
  "users",
  "products",
];

const getOpportunitySortOptions = (fieldConfigs) => {
  return SORTABLE_FIELD_ORDER.map((dataIndex) => fieldConfigs[dataIndex])
    .filter((config) => config && isFieldSortable(config))
    .map(({ dataIndex, title }) => ({
      value: dataIndex,
      label: title,
    }));
};

const normalizeSort = (sort, sortOptions) => {
  const hasDataIndex = sortOptions.some(({ value }) => value === sort?.dataIndex);
  const hasOrder = SORT_ORDER_OPTIONS.some(({ value }) => value === sort?.order);

  return {
    dataIndex: hasDataIndex ? sort.dataIndex : DEFAULT_SORT.dataIndex,
    order: hasOrder ? sort.order : DEFAULT_SORT.order,
  };
};

const SortControls = ({ sort, sortOptions, setSort }) => {
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "0px",
      }}
    >
      <div style={{ color: "grey", whiteSpace: "nowrap" }}>Sort By:</div>
      <Select
        filterOption={filterOption}
        onChange={(dataIndex) => {
          setSort({
            ...sort,
            dataIndex,
          });
        }}
        options={sortOptions}
        optionFilterProp="label"
        showSearch
        size="small"
        style={{ width: "200px" }}
        value={sort.dataIndex}
      />
      <Select
        onChange={(order) => {
          setSort({
            ...sort,
            order,
          });
        }}
        options={SORT_ORDER_OPTIONS}
        size="small"
        style={{ width: "140px" }}
        value={sort.order}
      />
    </div>
  );
};

export const Opportunities = (props) => {
  const { account } = props;

  const queryClient = useQueryClient();
  const opportunities = useOpportunities();
  const organization = useOrganization();
  const fieldConfigs = useFieldConfigs(viewTypes.OPPORTUNITY);
  const lossReasons = useLossReasons();
  const opportunityStates = useOpportunityStates();
  const products = useProducts();
  const salesProcesses = useSalesProcesses();
  const users = useUsers();

  const { modal, open } = useModal(CreateOpportunityModal);

  const sortOptions = useMemo(() => {
    return getOpportunitySortOptions(fieldConfigs);
  }, [fieldConfigs]);

  const [storedSort, setStoredSort] = useLocalStorage(
    `${organization._id}.LAYOUT_STATE.ACCOUNT.OPPORTUNITIES_SORT`,
    DEFAULT_SORT
  );

  const sort = normalizeSort(storedSort, sortOptions);

  const filteredOpportunities = opportunities
    .all()
    .filter(({ accountId }) => accountId === account._id);

  const sortedOpportunities = useMemo(() => {
    return sortData({
      data: filteredOpportunities,
      cache: {
        accounts: { entities: { [account._id]: account }, key: "name" },
        lossReasons: { entities: lossReasons.entities, key: "label" },
        opportunityStates: {
          entities: opportunityStates.entities,
          key: "label",
        },
        products: {
          entities: products.entities,
          key: "name",
        },
        salesProcesses: { entities: salesProcesses.entities, key: "name" },
        users: { entities: users.entities, key: "alias" },
      },
      fieldConfigs,
      sortOrder: [sort],
    });
  }, [
    account,
    fieldConfigs,
    filteredOpportunities,
    lossReasons.entities,
    opportunityStates.entities,
    products.entities,
    salesProcesses.entities,
    sort,
    users.entities,
  ]);

  return (
    <>
      {modal}
      <OpportunitiesList
        controls={
          <SortControls
            sort={sort}
            sortOptions={sortOptions}
            setSort={setStoredSort}
          />
        }
        opportunities={sortedOpportunities}
        openCreateModal={() =>
          open({
            onCreate: async () => {
              await queryClient.invalidateQueries({ queryKey: [accountsRoot] });
            },
            preselectedAccountId: account._id,
          })
        }
      />
    </>
  );
};
